// File: src/components/HistoryCarousel.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/History_carousel.css";
import bigLeftArrow from '../images/bigLeftArrow.png';
import bigRightArrow from '../images/bigRightArrow.png';
import emptyStateIcon from "../images/emptyStateIcon.png";
import { fetchUserLibraryBooks } from "../services/AllServices.js";
import { speakText } from './utils/speechUtils.js';

const HistoryCarousel = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [historyBooks, setHistoryBooks] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Calculate visibility states
  const maxIndex = Math.max(0, historyBooks.length - 7);
  const showArrows = historyBooks.length > 7; // Show arrows when more than 7 books
  const isBeginning = currentIdx === 0;
  const isEnd = currentIdx >= maxIndex;

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await fetchUserLibraryBooks();
        if (response.status === "success") {
          const filteredBooks = response.data.filter(
            book => book.status === "completed" || book.status === "restarted"
          );
          setBooks(response?.data || []);
          setHistoryBooks(filteredBooks);
        }
      } catch (error) {
        console.error("Error fetching history books:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const prevSlide = () => {
    setCurrentIdx((prevIdx) => Math.max(0, prevIdx - 1));
  };

  const nextSlide = () => {
    setCurrentIdx((prevIdx) => Math.min(maxIndex, prevIdx + 1));
  };

  if (loading) {
    return (
      <div className="carousel__wrap">
        <div className="carousel__skeleton-loader-lib">
          {[...Array(7)].map((_, index) => (
            <div key={index} className="carousel__skeleton-item"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="carousel__wrap">
      {historyBooks.length === 0 ? (
        <div className="empty-state-container">
          <div
            className="genre_heading"
            tabIndex={0}
            onFocus={() =>
              speakText(
                `History. You have ${historyBooks.length} books in history.`
              )
            }
          >
            History
          </div>
          <div className="empty-state">
            <div className="emptyStateIconAndHeading">
              <div className="emptyStateIcon">
                <img src={emptyStateIcon} alt="No books" />
              </div>
              <div className="empty-state__heading">No Books in History</div>
            </div>
            <p className="empty-state__text">
              Start listening to your favorite books to see them here.
            </p>
            <a href="/" className="empty-state__cta">
              Browse Books
            </a>
          </div>
        </div>
      ) : (
        <div className="carousel__inner" role="region" aria-label="History carousel">
          <button
            className={`carousel__btn ${isBeginning ? 'disabled' : ''}`}
            onClick={prevSlide}
            disabled={isBeginning}
            tabIndex={isBeginning ? -1 : 0}
            onFocus={() => speakText("Previous books")}
            aria-label="Previous books"
            type="button"
          >
            <div className="carousel__btn--prev">
              {showArrows && (
                <img src={bigLeftArrow} alt="Previous" className="carousel__btn-arrow" />
              )}
            </div>
          </button>

          <div className="carousel__container">
            <div
              className="genre_heading"
              tabIndex={0}
              onFocus={() =>
                speakText(
                  `History. You have ${historyBooks.length} books in history.`
                )
              }
            >
              History
            </div>

            <div
              className="carousel__slide-list"
              style={{ transform: `translateX(-${currentIdx * (10.3 + 2)}vw)` }}
            >
              {historyBooks.map((book) => (
                <div key={book._id} className="carousel__slide-item">
                  <button
                    className="carousel__slide-button"
                    type="button"
                    onClick={() => navigate(`/book-info/${book._id}`, { state: { books } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/book-info/${book._id}`, { state: { books } });
                      }
                    }}
                    onFocus={() => speakText(book.title)}
                    aria-label={`Open ${book.title}`}
                  >
                    <img src={book.thumbnail} alt={book.title} className="carousel__slide-img" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            className={`carousel__btn ${isEnd ? 'disabled' : ''}`}
            onClick={nextSlide}
            disabled={isEnd}
            tabIndex={isEnd ? -1 : 0}
            onFocus={() => speakText("Next books")}
            aria-label="Next books"
            type="button"
          >
            <div className="carousel__btn--next">
              {showArrows && (
                <img src={bigRightArrow} alt="Next" className="carousel__btn-arrow" />
              )}
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoryCarousel;
